import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Chip,
  Container,
  Divider,
  Drawer,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  fetchReportsInventory,
  fetchReportsOrders,
  fetchReportsProducts,
} from '../../services/screenApi';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import useScreenAuth from '../../components/useScreenAuth';
import { clearScreenAuthData } from '../../utils/screenAuthStorage';

const slideIntervalMs = 10000;
const TOTAL_SLIDES = 6;
const accentColors = {
  success: '#1e824c',
  warning: '#f0b429',
  info: '#1d72b8',
  danger: '#d64541',
};

const softBg = {
  success: '#e8f5ef',
  warning: '#fff4e0',
  info: '#e8f1fb',
  danger: '#fdecea',
};

const formatCurrency = (value, { withCurrency = true } = {}) => {
  const amount = Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return withCurrency ? `${amount} DHS` : amount;
};

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const truncateSx = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const tableContainerSx = { maxHeight: 'calc(100vh - 320px)' };
const tablePaperSx = {
  borderRadius: 2.5,
  boxShadow: '0px 8px 24px rgba(0,0,0,0.08)',
  overflow: 'hidden',
};
const headCellSx = { fontSize: '1rem', fontWeight: 700 };
const bodyCellSx = { fontSize: '1rem' };

const ScreenReportsView = () => {
  const { token, isAuthenticated, isLoading: isAuthLoading, store_ids } = useScreenAuth();
  const [reports, setReports] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [productDetail, setProductDetail] = useState(null);
  const [inventoryDetail, setInventoryDetail] = useState(null);
  const [storesById, setStoresById] = useState({});
  const navigate = useNavigate();

  const normalizeProductsReport = (payload = {}) => {
    const root = payload?.data || payload;

    return {
      dateRange: root?.dateRange || null,
      productsAdded: root?.productsAdded || 0,
      productsByPlatform: root?.productsByPlatform || [],
      highestUnitPriceSold: root?.highestUnitPriceSold || null,
      highestRevenueProduct: root?.highestRevenueProduct || null,
      mostSoldByQuantity: root?.mostSoldByQuantity || null,
      isComposantFalseAndEmptyComponents: root?.isComposantFalseAndEmptyComponents || null,
    };
  };

  const normalizeOrdersReport = (payload = {}) => {
    const root = payload?.data || payload;
    const summary = root?.ordersSummary || root?.orders_summary || {};
    const summaryByOrderType = root?.summaryByOrderType || root?.summary_by_order_type || [];
    const aggregatedOrderCount = summaryByOrderType.reduce((sum, item) => sum + Number(item?.orderCount || 0), 0);
    const aggregatedTotalAmount = summaryByOrderType.reduce((sum, item) => sum + Number(item?.totalAmount || 0), 0);

    const totalOrders = summary?.totalOrders || aggregatedOrderCount;
    const hasTotalAmountMismatch =
      aggregatedTotalAmount > 0 &&
      summary?.totalOrderAmount &&
      Math.abs(Number(summary.totalOrderAmount) - aggregatedTotalAmount) > 0.5;
    const totalOrderAmount =
      hasTotalAmountMismatch || summary?.totalOrderAmount == null ? aggregatedTotalAmount : summary.totalOrderAmount;
    const averageBasket =
      totalOrders > 0
        ? hasTotalAmountMismatch || summary?.averageBasket == null
          ? Number(totalOrderAmount) / totalOrders
          : summary.averageBasket
        : summary?.averageBasket || 0;

    return {
      dateRange: root?.dateRange || null,
      ordersSummary: {
        totalOrders,
        ordersWithInventoryReserved: summary?.ordersWithInventoryReserved || 0,
        reservedProductsCount: summary?.reservedProductsCount || 0,
        totalOrderAmount,
        averageBasket,
      },
      summaryByOrderType,
      reservedInventoryByStore: root?.reservedInventoryByStore || root?.reserved_inventory_by_store || [],
    };
  };

  const normalizeInventoryReport = (payload = {}) => {
    const root = payload?.data || payload;
    const inventory = root?.inventoryBalancesCreated || root?.inventory_balances_created || {};

    return {
      dateRange: root?.dateRange || null,
      inventoryBalancesCreated: {
        count: inventory?.count || 0,
        examples: inventory?.examples || [],
      },
    };
  };

  const loadStores = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_PEGASUS_API_URL}/distinctStoreIds`, {
        headers: {
          ENCRYKEY: process.env.REACT_APP_KEY_PEGASUS,
        },
      });
      const list = res?.data?.response || [];
      const mapped = list.reduce((acc, store) => {
        const id = store?._id;
        const name = store?.name || store?.storeName || store?.storeCode;
        if (id) {
          acc[id] = name;
        }
        return acc;
      }, {});
      setStoresById(mapped);
    } catch (error) {
      console.error('Unable to load store names for reports', error);
    }
  }, []);

  const loadReports = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const params = Array.isArray(store_ids) && store_ids.length
        ? { store_ids: store_ids.join(',') }
        : {};

      const [productsRes, ordersRes, inventoryRes] = await Promise.all([
        fetchReportsProducts(token, params),
        fetchReportsOrders(token, params),
        fetchReportsInventory(token, params),
      ]);

      setReports({
        products: normalizeProductsReport(productsRes),
        orders: normalizeOrdersReport(ordersRes),
        inventory: normalizeInventoryReport(inventoryRes),
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error?.response?.status === 401) {
        clearScreenAuthData();
        navigate('/screens/login');
      } else {
        console.error('Unable to fetch screen reports', error);
      }
    }
  }, [navigate, store_ids, token]);

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      navigate('/screens/login');
    }
  }, [isAuthLoading, isAuthenticated, navigate]);

  useEffect(() => {
    loadReports();
    loadStores();
    const intervalId = setInterval(loadReports, 60000);
    return () => clearInterval(intervalId);
  }, [loadReports, loadStores]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % TOTAL_SLIDES);
    }, slideIntervalMs);

    return () => clearInterval(timer);
  }, []);

  const ordersSummary = useMemo(() => reports?.orders?.ordersSummary || {}, [reports]);
  const summaryByOrderType = useMemo(() => reports?.orders?.summaryByOrderType || [], [reports]);
  const reservedInventoryByStore = useMemo(() => reports?.orders?.reservedInventoryByStore || [], [reports]);
  const productsReport = useMemo(() => reports?.products || {}, [reports]);
  const inventoryReport = useMemo(() => reports?.inventory || {}, [reports]);
  const inventoryExamples = useMemo(
    () => inventoryReport?.inventoryBalancesCreated?.examples || [],
    [inventoryReport]
  );
  const productsExamples = useMemo(
    () => productsReport?.isComposantFalseAndEmptyComponents?.examples || [],
    [productsReport]
  );
  const dateRange =
    reports?.orders?.dateRange || reports?.products?.dateRange || reports?.inventory?.dateRange || null;

  const inventoryTotalQty = useMemo(
    () => inventoryExamples.reduce((sum, item) => sum + Number(item?.qty || 0), 0),
    [inventoryExamples]
  );
  const stockStateDistribution = useMemo(() => {
    const counts = inventoryExamples.reduce((acc, item) => {
      const state = item?.stock_state || 'Inconnu';
      acc[state] = (acc[state] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts).map(([state, count]) => ({ state, count }));
  }, [inventoryExamples]);
  const totalStockStates = useMemo(
    () => stockStateDistribution.reduce((sum, item) => sum + item.count, 0),
    [stockStateDistribution]
  );

  const latestMovementTs = useMemo(() => {
    const timestamps = inventoryExamples
      .map((item) => item?.last_movement_ts || item?.lastMovementTs || item?.createdAt)
      .filter(Boolean)
      .map((value) => new Date(value).getTime());
    return timestamps.length ? new Date(Math.max(...timestamps)).toISOString() : null;
  }, [inventoryExamples]);

  const resolveStoreName = (storeIdObj) => {
    const directName = storeIdObj?.storeName || storeIdObj?.storeCode || storeIdObj?.name;
    const id = storeIdObj?._id || storeIdObj;
    return storesById[id] || directName || 'Magasin inconnu';
  };

  if (isAuthLoading) {
    return null;
  }

  const loadingSkeleton = (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Stack spacing={3}>
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
      </Stack>
    </Container>
  );

  if (isLoading || !reports) {
    return loadingSkeleton;
  }

  const kpiCardSx = (colorKey = 'info') => ({
    p: 3,
    borderRadius: 3,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: '0px 8px 24px rgba(0,0,0,0.08)',
    border: `1px solid ${(accentColors[colorKey] || '#dfe6ed')}22`,
    backgroundColor: softBg[colorKey] || '#ffffff',
  });

  const IconBadge = ({ colorKey = 'info', children }) => (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2.5,
        backgroundColor: `${accentColors[colorKey] || '#1d72b8'}14`,
        color: accentColors[colorKey] || '#1d72b8',
      }}
    >
      {children}
    </Stack>
  );

  const CurrencyValue = ({ amount, variant = 'h3' }) => (
    <Stack spacing={0.25} alignItems="flex-start">
      <Typography variant={variant} color="text.primary" fontWeight={800} sx={{ lineHeight: 1.1 }}>
        {formatCurrency(amount, { withCurrency: false })}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ lineHeight: 1 }}>
        DHS
      </Typography>
    </Stack>
  );

  const KpiCard = ({ label, value, colorKey = 'info', icon = null, valueVariant = 'h3', children }) => (
    <Paper elevation={0} sx={kpiCardSx(colorKey)}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {icon && <IconBadge colorKey={colorKey}>{icon}</IconBadge>}
          <Stack spacing={0.5} flex={1}>
            <Typography variant="subtitle2" color="text.secondary" sx={truncateSx}>
              {label}
            </Typography>
            {React.isValidElement(value) ? (
              value
            ) : (
              <Typography variant={valueVariant} color="text.primary" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {value ?? '—'}
              </Typography>
            )}
          </Stack>
        </Stack>
        {children && <Stack spacing={0.5}>{children}</Stack>}
      </Stack>
    </Paper>
  );

  const overviewSlide = (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Synthèse des rapports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dateRange ? `${formatDateTime(dateRange.start)} → ${formatDateTime(dateRange.end)}` : 'Période courante'}
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          {[{ label: 'Produits ajoutés', value: productsReport.productsAdded, colorKey: 'info', icon: <Inventory2Outlined fontSize="medium" /> },
          { label: 'Commandes', value: ordersSummary.totalOrders, colorKey: 'info', icon: <ShoppingCartOutlined fontSize="medium" /> },
          { label: 'Montant commandes', value: <CurrencyValue amount={ordersSummary.totalOrderAmount} />, colorKey: 'success', icon: <PaidOutlined fontSize="medium" /> },
          { label: 'Balances inventaire', value: inventoryReport?.inventoryBalancesCreated?.count || 0, colorKey: 'success', icon: <AssessmentOutlined fontSize="medium" /> },
          { label: 'Articles simples à vérifier', value: productsReport?.isComposantFalseAndEmptyComponents?.count || 0, colorKey: 'warning', icon: <WarningAmberOutlined fontSize="medium" /> },
          { label: 'Quantité totale inventaire', value: inventoryTotalQty, colorKey: 'info', icon: <StorefrontOutlined fontSize="medium" /> }].map((kpi) => (
            <Grid item xs={12} sm={6} md={4} key={kpi.label}>
              <KpiCard
                label={kpi.label}
                value={kpi.value}
                colorKey={kpi.colorKey}
                icon={kpi.icon}
              />
            </Grid>
          ))}
        </Grid>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: `1px solid ${(accentColors.info || '#dfe6ed')}33`,
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={800} color="white">
                Répartition des statuts de stock
              </Typography>
              <Chip
                label={`${totalStockStates} mouvements`}
                size="medium"
                color="info"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  color: 'white',
                  borderRadius: 2,
                  fontWeight: 700,
                }}
              />
            </Stack>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              Vue grand écran optimisée pour les passages en magasin.
            </Typography>
          </Stack>
          {stockStateDistribution.length ? (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {stockStateDistribution.map((item) => {
                const percentage = totalStockStates ? Math.round((item.count / totalStockStates) * 100) : 0;
                return (
                  <Stack key={item.state} spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={800} color="white">
                        {item.state}
                      </Typography>
                      <Typography variant="subtitle1" color="white" fontWeight={700}>
                        {item.count} ({percentage}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 12,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.12)',
                        '& .MuiLinearProgress-bar': { backgroundColor: softBg.info },
                      }}
                    />
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body1" color="rgba(255,255,255,0.85)" sx={{ mt: 2 }}>
              Aucun mouvement d'inventaire pour la période sélectionnée.
            </Typography>
          )}
        </Paper>
      </Stack>
    </Container>
  );

  const productsKpiSlide = (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Produits — Indicateurs clés
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {productsReport?.dateRange
              ? `${formatDateTime(productsReport.dateRange.start)} → ${formatDateTime(productsReport.dateRange.end)}`
              : 'Période courante'}
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Produits ajoutés"
              value={productsReport.productsAdded || 0}
              colorKey="info"
              icon={<Inventory2Outlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Prix unitaire maximum"
              value={<CurrencyValue amount={productsReport?.highestUnitPriceSold?.unitPrice || 0} />}
              colorKey="success"
              icon={<PaidOutlined fontSize="medium" />}
            >
              <Tooltip title={productsReport?.highestUnitPriceSold?.title || ''} placement="top" arrow>
                <Typography variant="body2" color="text.secondary" sx={truncateSx}>
                  {productsReport?.highestUnitPriceSold?.title || '—'}
                </Typography>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {productsReport?.highestUnitPriceSold?.orderId || '—'} — {formatDateTime(productsReport?.highestUnitPriceSold?.date)}
              </Typography>
            </KpiCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Plus forte recette"
              value={<CurrencyValue amount={productsReport?.highestRevenueProduct?.revenue || 0} />}
              colorKey="success"
              icon={<TrendingUpOutlined fontSize="medium" />}
            >
              <Tooltip title={productsReport?.highestRevenueProduct?.title || ''} placement="top" arrow>
                <Typography variant="body2" color="text.secondary" sx={truncateSx}>
                  {productsReport?.highestRevenueProduct?.title || '—'}
                </Typography>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                Qté: {productsReport?.highestRevenueProduct?.quantity || 0}
              </Typography>
            </KpiCard>
          </Grid>
            <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Produit le plus vendu"
              value={productsReport?.mostSoldByQuantity?.quantity || 0}
              colorKey="info"
              icon={<InsightsOutlined fontSize="medium" />}
            >
              <Tooltip title={productsReport?.mostSoldByQuantity?.title || ''} placement="top" arrow>
                <Typography variant="body2" color="text.secondary" sx={truncateSx}>
                  {productsReport?.mostSoldByQuantity?.title || '—'}
                </Typography>
              </Tooltip>
            </KpiCard>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          {(productsReport.productsByPlatform || []).map((platform) => (
            <Chip
              key={platform.platform || platform.name}
              label={`${platform.platform || platform.name}: ${platform.count || 0}`}
              color="info"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          ))}
          {!productsReport.productsByPlatform?.length && (
            <Chip label="Aucune plateforme renseignée" size="small" variant="outlined" />
          )}
        </Stack>
      </Stack>
    </Container>
  );

  const productsQualitySlide = (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Produits — Qualité des données
          </Typography>
          <Chip
            label={`${productsReport?.isComposantFalseAndEmptyComponents?.count || 0} articles simples sans composants`}
            color="warning"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          />
        </Stack>
        <Paper elevation={0} sx={kpiCardSx('warning')}>
          <Typography variant="subtitle2" color="text.secondary">
            Vérifiez les produits dont « isComposant = false » et sans composants associés.
          </Typography>
        </Paper>
        <Paper elevation={1} sx={tablePaperSx}>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Produit</TableCell>
                  <TableCell sx={headCellSx}>Prix régulier / réduit</TableCell>
                  <TableCell sx={headCellSx}>Disponibilité</TableCell>
                  <TableCell sx={headCellSx}>Magasins</TableCell>
                  <TableCell sx={headCellSx}>Plateformes</TableCell>
                  <TableCell sx={headCellSx}>Mise à jour</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productsExamples.length ? (
                  productsExamples.map((product) => (
                    <TableRow
                      key={product._id}
                      hover
                      onClick={() => setProductDetail(product)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell width="25%" sx={bodyCellSx}>
                        <Tooltip title={product?.name || product?.title || ''} arrow>
                          <Typography variant="body1" sx={truncateSx}>
                            {product?.name || product?.title || 'Produit'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {formatCurrency(product?.price || product?.regularPrice || 0)}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {product?.discountPrice ? formatCurrency(product.discountPrice) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          label={product?.availability || product?.isAvailable ? 'Disponible' : 'À vérifier'}
                          size="medium"
                          color={product?.availability || product?.isAvailable ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {product?.stores?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {product?.plateforms?.length || product?.platforms?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {formatDateTime(product?.updatedAt || product?.date)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={bodyCellSx}>
                      <Typography variant="body1" color="text.secondary">
                        Aucun produit concerné pour la période.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Container>
  );

  const ordersSlide = (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Commandes — Synthèse
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reports?.orders?.dateRange
              ? `${formatDateTime(reports.orders.dateRange.start)} → ${formatDateTime(reports.orders.dateRange.end)}`
              : 'Période courante'}
          </Typography>
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Total commandes"
              value={ordersSummary.totalOrders || 0}
              colorKey="info"
              icon={<ShoppingCartOutlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Commandes avec stock réservé"
              value={ordersSummary.ordersWithInventoryReserved || 0}
              colorKey="warning"
              icon={<Inventory2Outlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Montant total"
              value={<CurrencyValue amount={ordersSummary.totalOrderAmount || 0} />}
              colorKey="success"
              icon={<PaidOutlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Panier moyen"
              value={<CurrencyValue amount={ordersSummary.averageBasket || 0} />}
              colorKey="info"
              icon={<InsightsOutlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Produits réservés"
              value={ordersSummary.reservedProductsCount || 0}
              colorKey="info"
              icon={<StorefrontOutlined fontSize="medium" />}
            />
          </Grid>
        </Grid>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: `1px solid ${(accentColors.info || '#dfe6ed')}33`,
            background: 'linear-gradient(145deg, #eef5fc, #ffffff)',
            boxShadow: '0 12px 30px rgba(29,114,184,0.08)',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={800} color="text.primary">
              Répartition par type de commande
            </Typography>
            <Chip
              label={`${summaryByOrderType.length} types`}
              size="medium"
              color="info"
              variant="outlined"
              sx={{ borderRadius: 2, fontWeight: 700, px: 1.25 }}
            />
          </Stack>
          <Table
            size="medium"
            sx={{
              '& th': { fontSize: '1.05rem', fontWeight: 800, letterSpacing: 0.2 },
              '& td': { fontSize: '1.05rem', py: 1.25 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell align="right">Commandes</TableCell>
                <TableCell align="right">Montant total</TableCell>
                <TableCell align="right">Panier moyen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryByOrderType.length ? (
                summaryByOrderType.map((item) => (
                  <TableRow
                    key={item.productType || item.productTypeUpper}
                    hover
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#f7fbff' },
                      '&:hover': { backgroundColor: '#e8f3ff' },
                    }}
                  >
                    <TableCell width="30%">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={item.productTypeUpper || item.productType || 'Type'}
                          size="medium"
                          color="info"
                          variant="outlined"
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        />
                        <Typography variant="subtitle1" color="text.secondary" sx={truncateSx} fontWeight={700}>
                          {item.productType || 'Type inconnu'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" color="text.primary" fontWeight={800}>
                        {item.orderCount ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" color="text.primary" fontWeight={800}>
                        {formatCurrency(item.totalAmount || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" color="text.secondary" fontWeight={700}>
                        {formatCurrency(item.avgBasket || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Aucune répartition par type disponible pour cette période.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: `1px solid ${(accentColors.info || '#dfe6ed')}33`,
            backgroundColor: '#0f172a',
            color: 'white',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={800}>
              Réservations par magasin
            </Typography>
            <Chip
              label={`${reservedInventoryByStore.length} magasins`}
              size="medium"
              color="info"
              sx={{ borderRadius: 2, fontWeight: 700, backgroundColor: 'rgba(255,255,255,0.12)', color: 'white' }}
            />
          </Stack>
          {reservedInventoryByStore.length ? (
            <Stack spacing={1.5}>
              {reservedInventoryByStore.map((item) => (
                <Stack
                  key={item.storeId || item.storeName}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={0.5}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={800}>
                    {item.storeName || item.storeId || 'Magasin'}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body1" fontWeight={700}>
                      Commandes réservées : {item.ordersWithReserved ?? '—'}
                    </Typography>
                    <Chip
                      label={`${item.reservedProductsCount ?? 0} produits`}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ borderRadius: 2, color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
                    />
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body1" color="rgba(255,255,255,0.85)">
              Aucun stock réservé par magasin sur la période.
            </Typography>
          )}
        </Paper>
      </Stack>
    </Container>
  );

  const inventoryKpiSlide = (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Inventaire — Indicateurs clés
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Balances créées"
              value={inventoryReport?.inventoryBalancesCreated?.count || 0}
              colorKey="success"
              icon={<AssessmentOutlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Quantité totale"
              value={inventoryTotalQty}
              colorKey="info"
              icon={<StorefrontOutlined fontSize="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Dernier mouvement"
              value={latestMovementTs ? formatDateTime(latestMovementTs) : '—'}
              colorKey="info"
              icon={<TrendingUpOutlined fontSize="medium" />}
              valueVariant="h5"
            />
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );

  const inventoryMovementsSlide = (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: 'calc(100vh - 120px)' }}>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Inventaire — Mouvements
          </Typography>
        </Stack>
        <Paper elevation={1} sx={tablePaperSx}>
          <TableContainer sx={tableContainerSx}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Magasin</TableCell>
                  <TableCell sx={headCellSx}>Produit</TableCell>
                  <TableCell sx={headCellSx}>Statut</TableCell>
                  <TableCell sx={headCellSx}>Quantité</TableCell>
                  <TableCell sx={headCellSx}>Créé le</TableCell>
                  <TableCell sx={headCellSx}>Dernier mouvement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryExamples.length ? (
                  inventoryExamples.map((item) => (
                    <TableRow
                      key={item._id}
                      hover
                      onClick={() => setInventoryDetail(item)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell width="20%" sx={bodyCellSx}>
                        <Tooltip title={resolveStoreName(item.store_id)} arrow>
                          <Typography variant="body1" sx={truncateSx}>
                            {resolveStoreName(item.store_id)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell width="25%" sx={bodyCellSx}>
                        <Tooltip title={item?.product_id?.name || item?.product_id || ''} arrow>
                          <Typography variant="body1" sx={truncateSx}>
                            {item?.product_id?.name || item?.product_id?._id || 'Produit'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          label={item?.stock_state || 'N/A'}
                          size="medium"
                          variant="outlined"
                          color="info"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {item?.qty ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {formatDateTime(item?.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          {formatDateTime(item?.last_movement_ts || item?.lastMovementTs)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={bodyCellSx}>
                        <Typography variant="body1" color="text.secondary">
                          Aucun mouvement d'inventaire disponible.
                        </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Container>
  );

  const slides = [
    overviewSlide,
    productsKpiSlide,
    productsQualitySlide,
    ordersSlide,
    inventoryKpiSlide,
    inventoryMovementsSlide,
  ];

  return (
    <Stack sx={{ minHeight: '100vh', backgroundColor: '#f7f9fc' }} spacing={2}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={800} color="text.primary">
            Tableaux de bord — écrans
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {slides.map((_, index) => (
              <Chip
                key={index}
                onClick={() => setActiveSlide(index)}
                color={index === activeSlide ? 'info' : 'default'}
                variant={index === activeSlide ? 'filled' : 'outlined'}
                size="small"
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        </Stack>
      </Container>
      <Divider />
      {slides[activeSlide]}

      <Drawer anchor="right" open={Boolean(productDetail)} onClose={() => setProductDetail(null)}>
        <Stack spacing={2} sx={{ width: { xs: 320, sm: 420 }, p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Détails produit
          </Typography>
          {productDetail ? (
            <Stack spacing={1.5}>
              <Tooltip title={productDetail?.name || productDetail?.title || ''} arrow>
                <Typography variant="subtitle1" sx={truncateSx}>
                  {productDetail?.name || productDetail?.title || 'Produit'}
                </Typography>
              </Tooltip>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                Prix régulier : {formatCurrency(productDetail?.price || productDetail?.regularPrice || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prix réduit : {productDetail?.discountPrice ? formatCurrency(productDetail.discountPrice) : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plateformes : {productDetail?.plateforms?.length || productDetail?.platforms?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Magasins : {productDetail?.stores?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Statut : {productDetail?.availability || productDetail?.isAvailable ? 'Disponible' : 'À vérifier'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mise à jour : {formatDateTime(productDetail?.updatedAt || productDetail?.date)}
              </Typography>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Composants
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {productDetail?.components?.length ? `${productDetail.components.length} composants` : 'Aucun composant'}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Drawer>

      <Drawer anchor="right" open={Boolean(inventoryDetail)} onClose={() => setInventoryDetail(null)}>
        <Stack spacing={2} sx={{ width: { xs: 320, sm: 420 }, p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Détails mouvement
          </Typography>
          {inventoryDetail ? (
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={truncateSx}>
                {resolveStoreName(inventoryDetail.store_id)}
              </Typography>
              <Divider />
              <Tooltip title={inventoryDetail?.product_id?.name || inventoryDetail?.product_id || ''} arrow>
                <Typography variant="body2" color="text.secondary" sx={truncateSx}>
                  Produit : {inventoryDetail?.product_id?.name || inventoryDetail?.product_id?._id || 'Produit'}
                </Typography>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                Statut : {inventoryDetail?.stock_state || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quantité : {inventoryDetail?.qty ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Créé le : {formatDateTime(inventoryDetail?.createdAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dernier mouvement : {formatDateTime(inventoryDetail?.last_movement_ts || inventoryDetail?.lastMovementTs)}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Drawer>
    </Stack>
  );
};

export default ScreenReportsView;
